import { useEffect, useRef, useState } from "react";

const INITIAL_WEEKS_TO_SHOW = 4;
const WEEKS_TO_LOAD_PER_SCROLL = 4;

export const useLazyLoadWeeks = (totalWeeks: number, open: boolean) => {
    const [visibleWeeksCount, setVisibleWeeksCount] = useState(INITIAL_WEEKS_TO_SHOW);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // State rather than a ref: the sentinel is unmounted every time the grid gives way to the empty
    // state, and comes back as a different node. A ref would not re-run the effect below, leaving
    // the observer watching the discarded node — it can never intersect again, so no week loads.
    const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) {
            setVisibleWeeksCount(INITIAL_WEEKS_TO_SHOW);
        }
    }, [open]);

    useEffect(() => {
        if (!sentinel) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];

                if (entry?.isIntersecting) {
                    setVisibleWeeksCount((prev) => prev + WEEKS_TO_LOAD_PER_SCROLL);
                }
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '200px',
                threshold: 0.1,
            }
        );

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [sentinel, visibleWeeksCount, open]);

    const hasMoreWeeks = visibleWeeksCount < totalWeeks;

    return {
        visibleWeeksCount,
        scrollContainerRef,
        sentinelRef: setSentinel,
        hasMoreWeeks
    };
};
